import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import AppLayout from './components/layout/AppLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './components/ui/ToastContext';
import { supabase } from './lib/supabase';
import { getCurrentUser, setUser, clearUser } from './store/slices/authSlice';
import { fetchWishlist, clearWishlist } from './store/slices/favoritesSlice';
import { fetchCartItems, clearCart, hydrateCartFromStorage, syncCartAsync } from './store/slices/cartSlice';
import { localCartService } from './services/localCartService';
import { userService } from './services';
import { logger } from '@/utils/logger';

const Home = lazy(() => import('./pages/Home'));
const Explore = lazy(() => import('./pages/Explore'));
const CategoryExplore = lazy(() => import('./pages/CategoryExplore'));
const Artists = lazy(() => import('./pages/Artists'));
const ArtistProfile = lazy(() => import('./pages/ArtistProfile'));
const News = lazy(() => import('./pages/News'));
const NewsDetail = lazy(() => import('./pages/NewsDetail'));
const CommunityProposals = lazy(() => import('./pages/CommunityProposals'));
const ArtworkDetail = lazy(() => import('./pages/ArtworkDetail'));
const Favorites = lazy(() => import('./pages/Favorites'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Auth = lazy(() => import('./pages/Auth'));
const Post = lazy(() => import('./pages/Post'));
const MyArtworks = lazy(() => import('./pages/MyArtworks'));
const Profile = lazy(() => import('./pages/Profile'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const Sales = lazy(() => import('./pages/Sales'));
const Settings = lazy(() => import('./pages/Settings'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentFailure = lazy(() => import('./pages/PaymentFailure'));
const PaymentPending = lazy(() => import('./pages/PaymentPending'));
const OrderDetailPage = lazy(() => import('./pages/OrderDetailPage'));
const OrdersListPage = lazy(() => import('./pages/OrdersListPage'));
const ArtistSetup = lazy(() => import('./pages/ArtistSetup'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const About = lazy(() => import('./pages/About'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Terms = lazy(() => import('./pages/legal/Terms'));
const Privacy = lazy(() => import('./pages/legal/Privacy'));
const Disputes = lazy(() => import('./pages/legal/Disputes'));
const IntellectualProperty = lazy(() => import('./pages/legal/IntellectualProperty'));
const CodeOfConduct = lazy(() => import('./pages/legal/CodeOfConduct'));

function App() {
  const dispatch = useDispatch();
  const { user, is_authenticated, initialized } = useSelector(state => state.auth);

  useEffect(() => {
    // Initialize authentication state on app startup
    dispatch(getCurrentUser());
  }, [dispatch]);

  /**
   * onAuthStateChange Listener - Synchronizes Supabase Auth with Redux
   *
   * Events:
   * - SIGNED_IN / TOKEN_REFRESHED → Update user in Redux (auto-refresh tokens, seamless UX)
   * - SIGNED_OUT → Clear user + cart + wishlist (supports remote logout)
   * - USER_UPDATED → Refresh user data (password changes invalidate old tokens)
   *
   * Edge cases:
   * - Profile fetch fails → Fallback to session.user
   * - Cleanup unsubscribes on unmount (prevents memory leaks)
   *
   * @see src/__tests__/App.test.jsx - 17 tests covering all events
   * @see https://supabase.com/docs/reference/javascript/auth-onauthstatechange
   */
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        logger.info('App', `onAuthStateChange: ${event}`, {
          user_id: session?.user?.id,
          user_email: session?.user?.email,
        });

        // Handle sign in and token refresh - NO getUserProfile calls
        // getCurrentUser handles all profile fetching with proper delays
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // DO NOTHING - getCurrentUser already handled user profile fetch
          // Calling Supabase here causes client to hang
          logger.debug('App', `${event} detected - skipping (getCurrentUser handles it)`);
        }

        // Handle logout event
        if (event === 'SIGNED_OUT') {
          // Clear Redux auth state
          dispatch(clearUser());

          // Clear wishlist and cart for logged out user
          dispatch(clearWishlist());
          dispatch(clearCart());
        }

        // Handle user update event (e.g., password change)
        if (event === 'USER_UPDATED') {
          if (session?.user) {
            // Password changed → old tokens are invalidated by Supabase
            // Update Redux with new session data
            const { data: profile_data, error: profile_error } = await userService.getUserProfile(session.user.id);

            if (!profile_error && profile_data) {
              dispatch(setUser({
                ...session.user,
                ...profile_data,
                profile: profile_data
              }));
            } else {
              dispatch(setUser(session.user));
            }
          }
        }
      }
    );

    // Cleanup: Unsubscribe from auth state changes on component unmount
    return () => {
      logger.debug('App', 'Cleanup: Unsubscribing from auth state changes');
      subscription?.unsubscribe();
    };
  }, [dispatch]);

  // Initialize cart and wishlist with smart persistence
  useEffect(() => {
    if (!initialized) return;

    const initializeCartAndWishlist = async () => {
      if (is_authenticated && user?.id) {
        // User is authenticated: Load from database + merge local if exists
        try {
          // Check if there's a local cart to merge
          const hasLocalCart = localCartService.hasCart();

          if (hasLocalCart) {
            logger.info('App', 'User authenticated with local cart - syncing with Backend API');

            // Load local cart and sync with Backend Custom API
            const localItems = localCartService.loadCart();

            // Call Backend Custom API /api/cart/sync to merge carts
            await dispatch(syncCartAsync({ local_items: localItems })).unwrap();

            // Clear local cart after successful sync
            localCartService.clearCart();

            logger.info('App', 'Cart synced with Backend API and localStorage cleared');
          } else {
            // No local cart, just load from Backend Custom API
            logger.debug('App', 'Loading cart from Backend Custom API');
            dispatch(fetchCartItems());
          }

          // Load wishlist
          dispatch(fetchWishlist(user.id));
        } catch (error) {
          logger.error('App', 'Error initializing authenticated cart', { error: error.message });

          // Fallback: Load from localStorage if database fails
          const localItems = localCartService.loadCart();
          if (localItems.length > 0) {
            dispatch(hydrateCartFromStorage(localItems));
          }
        }
      } else {
        // User not authenticated: Load from localStorage only
        logger.debug('App', 'Loading cart from localStorage (guest)');

        const localItems = localCartService.loadCart();
        dispatch(hydrateCartFromStorage(localItems));

        // Clear wishlist for non-authenticated users
        dispatch(clearWishlist());
      }
    };

    initializeCartAndWishlist();
  }, [dispatch, initialized, is_authenticated, user?.id]);

  return (
    <ToastProvider>
      <ErrorBoundary>
        <AppLayout>
        <div className="min-h-screen flex flex-col relative z-10">
          <ScrollToTop />
          <Navbar />
          <main className="flex-grow">
            <Suspense fallback={
              <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-raios-tertiary"></div>
              </div>
            }>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/explorar" element={<Explore />} />
                <Route path="/explorar/:slug" element={<CategoryExplore />} />
                <Route path="/artistas" element={<Artists />} />
                <Route path="/artistas/:id" element={<ArtistProfile />} />
                <Route path="/noticias" element={<News />} />
                <Route path="/noticias/:id" element={<NewsDetail />} />
                <Route path="/propuestas" element={<CommunityProposals />} />
                <Route path="/obra/:id" element={<ArtworkDetail />} />
                <Route path="/favoritos" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
                <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/publicar" element={<ProtectedRoute><Post /></ProtectedRoute>} />
                <Route path="/mis-obras" element={<ProtectedRoute><MyArtworks /></ProtectedRoute>} />
                <Route path="/pedidos" element={<Navigate to="/compras" replace />} />
                <Route path="/perfil" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/buscar" element={<SearchResults />} />
                <Route path="/ventas" element={<ProtectedRoute><Sales /></ProtectedRoute>} />
                <Route path="/configuracion" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/pago/exito" element={<PaymentSuccess />} />
                <Route path="/pago/error" element={<PaymentFailure />} />
                <Route path="/pago/pendiente" element={<PaymentPending />} />
                <Route path="/compras" element={<ProtectedRoute><OrdersListPage /></ProtectedRoute>} />
                <Route path="/compras/:order_id" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
                <Route path="/artista/setup" element={<ProtectedRoute><ArtistSetup /></ProtectedRoute>} />
                {/* Legal pages — public, no auth required */}
                <Route path="/sobre-raios" element={<About />} />
                <Route path="/terminos" element={<Terms />} />
                <Route path="/privacidad" element={<Privacy />} />
                <Route path="/devoluciones" element={<Disputes />} />
                <Route path="/propiedad-intelectual" element={<IntellectualProperty />} />
                <Route path="/codigo-de-conducta" element={<CodeOfConduct />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
        </AppLayout>
      </ErrorBoundary>
    </ToastProvider>
  );
}

export default App;